export interface Point {
  x: number;
  y: number;
}

/**
 * Math.cos/Math.sin are implementation-approximated in ECMAScript, so V8,
 * SpiderMonkey and JavaScriptCore disagree in the last ULP. Unrounded
 * coordinates therefore serialise to different attribute strings on the server
 * than in a non-Chromium browser, which React reports as a hydration mismatch.
 * Two decimals is ~1/200 of a CSS pixel at the viewBox scales used here.
 */
function snap(n: number): number {
  return Math.round(n * 100) / 100;
}

export function polarToCartesian(
  cx: number,
  cy: number,
  r: number,
  angleDeg: number,
): Point {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: snap(cx + r * Math.cos(rad)),
    y: snap(cy + r * Math.sin(rad)),
  };
}

export function arcStrokePath(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): string {
  const start = polarToCartesian(cx, cy, r, startDeg);
  const end = polarToCartesian(cx, cy, r, endDeg);
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

export function donutSlicePath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return [
    "M", outerStart.x, outerStart.y,
    "A", outerR, outerR, 0, largeArc, 1, outerEnd.x, outerEnd.y,
    "L", innerEnd.x, innerEnd.y,
    "A", innerR, innerR, 0, largeArc, 0, innerStart.x, innerStart.y,
    "Z",
  ].join(" ");
}

export function labelArcPath(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  let sa = startAngle;
  let ea = endAngle;
  if (ea < sa) ea += 360;
  const midAngle = ((sa + ea) / 2 + 360) % 360;
  const isBottom = midAngle > 90 && midAngle < 270;
  const largeArc = ea - sa > 180 ? 1 : 0;
  const start = polarToCartesian(cx, cy, r, sa);
  const end = polarToCartesian(cx, cy, r, ea);
  if (isBottom) {
    return `M ${end.x} ${end.y} A ${r} ${r} 0 ${largeArc} 0 ${start.x} ${start.y}`;
  }
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}
